import type { Line, Point } from "../coreComponents/Line";
import { createLine } from "../coreComponents/Line";
import { spatialRegistry } from "../interactionEngine/spatialRegistry";
import {
  mainLayer,
  lineLayer
} from "../../pixi-components/mainContainer/mainContainer";
import type { LineNodeWithMetaData } from "./lineRendererManager";
import { createLineRenderManager } from "./lineRendererManager";
import { getInstances } from "./renderer";
import type { FSCA, SCNode } from "./fsc.types";
import type { LineProps } from "../coreComponents/types";
import { NodeType } from "../../SComponents/scomponents.constants";
import {
  lineConstants,
  processFlowNodeConstants
} from "../../SComponents/scomponents.styles";
import { getViewport } from "../../pixi-components/layers/viewport/viewport.layer";

const { SWIMLANE_ROW_GAP, SWIMLANE_COLUMN_GAP, STEP_ROW_GAP, STEP_COLUMN_GAP } =
  processFlowNodeConstants;

const lineInstances: Record<string, Line> = {};
export const getLineInstances = () => lineInstances;

let lineRenderManager: ReturnType<typeof createLineRenderManager> | null = null;

export const lineRender = <T>(entryPoint: FSCA<T>, props: any) => {
  const lines = entryPoint(props) as SCNode<LineProps>[];
  if (!lineRenderManager) {
    lineRenderManager = createLineRenderManager();
  }

  const result = lineRenderManager.traverse(lines);

  // contains both added and updated lines
  const linesToUpdate = new Set<string>();

  for (const addedKey of result.added) {
    const lineNode = result.keyMap.get(addedKey);
    if (!lineNode) {
      throw Error(`Line node not found for key: ${addedKey}`);
    }

    if (lineInstances[addedKey]) {
      throw Error(`Line instance already exists for key: ${addedKey}`);
    }

    const line = createLine(lineNode.props);
    lineInstances[addedKey] = line;
    if (lineNode.props.flowLineType === "step") {
      mainLayer.addChild(line.pixiComponent);
    } else {
      lineLayer.addChild(line.pixiComponent);
    }
    linesToUpdate.add(addedKey);
  }

  for (const removedKey of result.removed) {
    const line = lineInstances[removedKey];
    if (!line) {
      throw Error(`Line instance not found for key: ${removedKey}`);
    }
    if (line.props.flowLineType === "step") {
      mainLayer.removeChild(line.pixiComponent);
    } else {
      lineLayer.removeChild(line.pixiComponent);
    }
    linesToUpdate.delete(removedKey);
    spatialRegistry.remove({
      key: line.props.key,
      layoutValues: line.getLayoutValues()
    });
    line.destroy();
    delete lineInstances[removedKey];
  }

  for (const updatedKey of result.updated) {
    const lineNode = result.keyMap.get(updatedKey);
    if (!lineNode) {
      throw Error(`Updated line node not found for key: ${updatedKey}`);
    }
    const line = lineInstances[updatedKey];
    if (!line) {
      throw Error(`Line instance not found for key: ${updatedKey}`);
    }
    line.updateProps(lineNode.props);
    linesToUpdate.add(updatedKey);
  }

  calculateAndRenderFlowLines(result.keyMap, linesToUpdate);
};

function calculateAndRenderFlowLines(
  keyMap: Map<string, LineNodeWithMetaData>,
  linesToUpdate: Set<string>
) {
  const viewport = getViewport();
  const visibleSectors = spatialRegistry.layoutToSectors(
    viewport.getVisibleBounds()
  );
  const nodeInstances = getInstances();
  const allFlowSwimlaneColumnKeys = Object.keys(nodeInstances).filter(key =>
    key.startsWith(NodeType.FLOW_SWIMLANE_COLUMN)
  );
  const allFlowSwimlaneColumnXPositions = allFlowSwimlaneColumnKeys
    .map(key => {
      const layout = nodeInstances[key].getLayoutValues();
      return layout.x + layout.width;
    })
    .sort((a, b) => b - a);

  linesToUpdate.forEach(key => {
    const line = lineInstances[key];
    const lineNode = keyMap.get(key);
    if (!lineNode) {
      throw Error(`Line node not found for key: ${key}`);
    }

    const startNode = nodeInstances[lineNode.props.startNodeId];
    const endNode = nodeInstances[lineNode.props.endNodeId];

    if (!startNode) {
      throw Error(`Start node not found for line: ${key}`);
    }
    if (!endNode) {
      throw Error(`End node not found for line: ${key}`);
    }
    const startLayout = startNode.getLayoutValues();
    const endLayout = endNode.getLayoutValues();

    const startX = startLayout.x + startLayout.width;
    const startY = startLayout.y + startLayout.height / 2;
    const startPoint = { x: startX, y: startY, height: startLayout.height };
    const endX = endLayout.x;
    const endY = endLayout.y + endLayout.height / 2;
    const endPoint = { x: endX, y: endY, height: endLayout.height };

    // If the line is selected, we want to render it on top of other lines
    const zIndex =
      lineNode.props.isSelected || lineNode.props.isHovered ? 1 : 0;

    //TODO for various scenarios the points will be different which we'll have to determine using instances and spatial registry
    const newLayoutValues = {
      startX,
      startY,
      endX,
      endY,
      intermediatePoints: getIntermediatePoints({
        startPoint,
        endPoint,
        allFlowSwimlaneColumnXPositions,
        flowLineType: lineNode.props.flowLineType
      }),
      zIndex,
      markerEndSize: getMarkerEndSize(lineNode.props.flowLineType),
      turningRadius: getTurningRadius(lineNode.props.flowLineType)
    };
    // get the last layout values
    const lastLayoutValues = line.getLayoutValues();

    line.setLayoutValues(newLayoutValues);
    // update the spatial registry
    spatialRegistry.update({
      key: line.props.key,
      lastLayoutValues,
      nextLayoutValues: line.getLayoutValues()
    });

    renderLine(line, visibleSectors);
  });
}

function renderLine(line: Line, visibleSectors: Set<string>) {
  const visibleKeys = new Set(
    spatialRegistry.retrieveFromSectors(new Set(visibleSectors))
  );
  const isStartNodeVisible = visibleKeys.has(line.props.startNodeId);
  const isEndNodeVisible = visibleKeys.has(line.props.endNodeId);

  if (isStartNodeVisible || isEndNodeVisible) {
    line.setCulled(false);
    line.render();
  } else {
    line.setCulled(true);
  }
}

export const lineUnrender = () => {
  Object.values(lineInstances).forEach(line => {
    if (line.props.flowLineType === "step") {
      mainLayer.removeChild(line.pixiComponent);
    } else {
      lineLayer.removeChild(line.pixiComponent);
    }
    spatialRegistry.remove({
      key: line.props.key,
      layoutValues: line.getLayoutValues()
    });
    line.destroy();
  });
  Object.keys(lineInstances).forEach(key => delete lineInstances[key]);
  if (lineRenderManager) {
    lineRenderManager.destroy();
    lineRenderManager = null;
  }
};

const getNearestVerticalAxisXPositionRightSide = (
  nodeX: number,
  allFlowSwimlaneColumnXPositions: number[]
) => {
  const nearestXPosition = allFlowSwimlaneColumnXPositions.reduce<number>(
    (nearest, current) => {
      if (nearest === 0) {
        return current;
      }
      if (nodeX < current && current < nearest) {
        return current;
      }
      return nearest;
    },
    0
  );
  return nearestXPosition;
};

type GetIntermediatePointsParams = {
  startPoint: Point & { height: number };
  endPoint: Point & { height: number };
  allFlowSwimlaneColumnXPositions: number[];
  flowLineType: LineProps["flowLineType"];
};

const getIntermediatePoints = ({
  startPoint,
  endPoint,
  allFlowSwimlaneColumnXPositions,
  flowLineType
}: GetIntermediatePointsParams): Point[] => {
  const intermediatePoints: Point[] = [];
  const isSwimlaneLine = flowLineType === "swimlane";

  const nearestVerticalAxisXPositionRightSide = isSwimlaneLine
    ? getNearestVerticalAxisXPositionRightSide(
        startPoint.x,
        allFlowSwimlaneColumnXPositions
      )
    : startPoint.x + STEP_COLUMN_GAP / 2;

  const isReverseFlowLine = endPoint.x - startPoint.x < 0;

  intermediatePoints.push({
    x: nearestVerticalAxisXPositionRightSide,
    y: startPoint.y
  });

  if (isReverseFlowLine) {
    const targetNodeTopY = endPoint.y - endPoint.height / 2;
    if (isSwimlaneLine) {
      const flowLineGapMiddleYPoint = targetNodeTopY - SWIMLANE_ROW_GAP / 2;
      intermediatePoints.push({
        x: nearestVerticalAxisXPositionRightSide,
        y: flowLineGapMiddleYPoint
      });
      const nearestVerticalAxisXPositionLeftSide =
        endPoint.x - SWIMLANE_COLUMN_GAP / 2;
      intermediatePoints.push({
        x: nearestVerticalAxisXPositionLeftSide,
        y: flowLineGapMiddleYPoint
      });

      intermediatePoints.push({
        x: nearestVerticalAxisXPositionLeftSide,
        y: endPoint.y
      });
    } else {
      const flowLineGapMiddleYPoint = targetNodeTopY - STEP_ROW_GAP / 2;
      intermediatePoints.push({
        x: nearestVerticalAxisXPositionRightSide,
        y: flowLineGapMiddleYPoint
      });
      const nearestVerticalAxisXPositionLeftSide =
        endPoint.x - STEP_COLUMN_GAP / 2;
      intermediatePoints.push({
        x: nearestVerticalAxisXPositionLeftSide,
        y: flowLineGapMiddleYPoint
      });

      intermediatePoints.push({
        x: nearestVerticalAxisXPositionLeftSide,
        y: endPoint.y
      });
    }
  } else {
    intermediatePoints.push({
      x: nearestVerticalAxisXPositionRightSide,
      y: endPoint.y
    });
  }

  return intermediatePoints;
};

const getMarkerEndSize = (flowLineType: LineProps["flowLineType"]) => {
  return flowLineType === "swimlane"
    ? lineConstants.SWIMLANE_MARKER_END_SIZE
    : lineConstants.STEP_MARKER_END_SIZE;
};

const getTurningRadius = (flowLineType: LineProps["flowLineType"]) => {
  return flowLineType === "swimlane"
    ? lineConstants.SWIMLANE_TURNING_RADIUS
    : lineConstants.STEP_TURNING_RADIUS;
};
